// chatUtils.js - Chat utility functions

/**
 * Format time ago from date
 * @param {Date} date - Date to format
 * @returns {string} - Formatted time string
 */
export const getTimeAgo = (date) => {
  // Handle null, undefined, or invalid dates
  if (!date) return 'Unknown';
  
  // Convert string dates to Date objects
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) return 'Invalid date';
  
  const now = new Date();
  const diffInMs = now - dateObj;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInDays < 7) return `${diffInDays}d ago`;
  return dateObj.toLocaleDateString();
};

/**
 * Strip file extension from filename
 * @param {string} name - Filename
 * @returns {string} - Filename without extension
 */
export const stripFileExtension = (name) => {
  if (!name) return "Source";
  const base = name.split(/[\\/]/).pop();
  return base.replace(/\.[^/.]+$/, "");
};

/**
 * Find the nearest preceding user question for a given message index
 * @param {Array} messages - Messages array
 * @param {number} msgIndex - Message index
 * @returns {string|null} - User question or null
 */
export const getPrevUserQuestion = (messages, msgIndex) => {
  if (!Array.isArray(messages)) return null;
  for (let i = msgIndex - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m && m.role === 'user' && typeof m.content === 'string') return m.content;
  }
  return null;
};

/**
 * Build unique sources from sources array
 * @param {Array} sources - Sources array
 * @returns {Array} - Unique sources
 */
export const buildUniqueSources = (sources) => {
  if (!Array.isArray(sources)) return [];
  const byLabel = new Map();
  for (const s of sources) {
    // Handle multiple possible filename field names
    const fileName = s.filename || s.documentName || s.name || s.title;
    const label = stripFileExtension(fileName) || 'Source';
    const prev = byLabel.get(label);
    if (!prev || (typeof s.similarity === 'number' && s.similarity > (prev.similarity ?? -Infinity))) {
      byLabel.set(label, s);
    }
  }
  return Array.from(byLabel.values());
};

/**
 * Extract source code from text
 * @param {string} text - Text to search
 * @returns {string|null} - Source code or null
 */
export const extractSourceCodeFromText = (text) => {
  if (!text) return null;
  const m = text.match(/\bSource:\s*([A-Za-z0-9._-]+)/i);
  return m ? m[1] : null;
};

/**
 * Pretty print JSON if possible
 * @param {string} raw - Raw text
 * @returns {string|null} - Formatted JSON or null
 */
export const prettyPrintJsonIfPossible = (raw) => {
  if (!raw) return null;
  const trimmed = raw.trim();
  
  // Heuristics: looks like JSON
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const obj = JSON.parse(trimmed);
      return JSON.stringify(obj, null, 2);
    } catch (e) {
      // Attempt to locate JSON substring
      try {
        const start = trimmed.indexOf('{');
        const end = trimmed.lastIndexOf('}');
        if (start >= 0 && end > start) {
          const sub = trimmed.slice(start, end + 1);
          const obj = JSON.parse(sub);
          return JSON.stringify(obj, null, 2);
        }
      } catch (_) {}
    }
  }
  return null;
};

/**
 * Normalize assistant text to plain text (strip markdown/asterisks, unify bullets)
 * @param {string} raw - Raw text
 * @returns {string} - Normalized text
 */
export const normalizeForDisplay = (raw) => {
  if (!raw || typeof raw !== 'string') return raw;
  let out = raw;
  
  // Remove markdown headings
  out = out.replace(/^[ \t]*#{1,6}[ \t]*/gm, '');
  
  // Normalize bullet markers at line start
  out = out.replace(/^[ \t]*[-*•·‣▪—–][ \t]+/gm, '• ');
  
  // Strip bold/italic/code markers
  out = out.replace(/\*\*(.*?)\*\*/g, '$1'); // **bold**
  out = out.replace(/(?<!\*)\*(?!\*)([^\n*][^\n]*?)\*(?!\*)/g, '$1'); // *italic*
  out = out.replace(/__(.*?)__/g, '$1'); // __bold__
  out = out.replace(/_([^_]+)_/g, '$1'); // _italic_
  out = out.replace(/`/g, ''); // inline code
  
  // Remove inline Source: lines, if any
  out = out.replace(/^\s*Source:\s*.*$/gim, '');
  
  // Collapse >2 blank lines
  out = out.replace(/\n{3,}/g, '\n\n');
  
  return out.trim();
};

/**
 * Render highlighted text with query matches
 * @param {string} text - Text to highlight
 * @param {string} queryText - Query to highlight
 * @returns {Object} - React component or text
 */
export const getHighlightedTextParts = (text, queryText) => {
  if (!text) return null;
  if (!queryText || typeof queryText !== 'string') {
    return { isPlain: true, text };
  }
  
  const tokens = (queryText.toLowerCase().match(/[a-z0-9]{3,}/g) || [])
    .filter((v, i, a) => a.indexOf(v) === i)
    .slice(0, 10);
    
  if (tokens.length === 0) {
    return { isPlain: true, text };
  }
  
  const pattern = new RegExp(`(${tokens.map(t => t.replace(/[.*+?^${}()|[\\]\\\\]/g, r => "\\"+r)).join('|')})`, 'ig');
  const parts = text.split(pattern);
  
  return {
    isPlain: false,
    parts: parts.map((part, idx) => ({
      text: part,
      isHighlighted: pattern.test(part),
      key: idx
    }))
  };
};

/**
 * Export chat history as text
 * @param {Array} messages - Messages array
 * @returns {string} - Formatted chat history
 */
export const exportChatHistory = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return '';
  }
  
  return messages
    .filter(msg => !msg.isTyping)
    .map(msg => `${msg.role === 'user' ? 'You' : 'AI Assistant'}: ${msg.content}`)
    .join('\n\n');
};

/**
 * Download text as file
 * @param {string} content - File content
 * @param {string} filename - Filename
 * @param {string} mimeType - MIME type
 */
export const downloadTextAsFile = (content, filename, mimeType = 'text/plain') => {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

/**
 * Generate session ID for chat
 * @returns {string} - Generated session ID
 */
export const generateSessionId = () => {
  return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};
